import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getMatchup, getLeagueScoreboard, getNflScoreboard } from '@severed-links/common.redherrings-reducers/football'

const useMatchup = ({ seasonId, seasonName, week, game }) => {
  const dispatch = useDispatch()
  const matchup = useSelector(state => (state.football.matchUps || []).find(item => item.seasonId === seasonId && item.week === week && item.game === game))
  const league = useSelector(state => (state.football.leagueScoreboards || []).find(item => item.seasonId === seasonId && item.week === week))
  const nfl = useSelector(state => (state.football.nflScoreboards || []).find(item => item.seasonId === seasonId && item.weekNumber === week && item.seasonMode !== 'Postseason'))
  const scoreboard = (league?.scoreboard || []).find(item => item.game === game) || matchup?.scoreboardItem || {}
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const queue = useRef(Promise.resolve())
  const request = useRef(0)
  const mounted = useRef(true)
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])

  const load = useCallback(async (includeScoreboards = true) => {
    if (!seasonId || !seasonName || !week || !game) return Promise.resolve()
    const current = ++request.current
    setLoading(true)
    setError('')
    // Serialize refreshes so an older response cannot replace a newer matchup.
    const operation = queue.current.catch(() => {}).then(async () => {
      if (!mounted.current || current !== request.current) return
      const results = await Promise.all([
        dispatch(getMatchup(seasonName, week, game)),
        ...(includeScoreboards ? [dispatch(getLeagueScoreboard(seasonName, week)), dispatch(getNflScoreboard(seasonId, week))] : []),
      ])
      if (results.some(result => !result || result.error)) throw new Error('Unable to refresh the matchup. Please try again.')
      if (!Array.isArray(results[0]?.payload?.nflGames)) throw new Error('Matchup details are not available yet.')
    })
    queue.current = operation
    try {
      await operation
    } catch (error) {
      if (mounted.current && current === request.current) setError(error.message || 'Unable to refresh the matchup. Please try again.')
    }
    if (mounted.current && current === request.current) setLoading(false)
  }, [dispatch, seasonId, seasonName, week, game])

  useEffect(() => { load() }, [load])
  // Compare actual scores/status, never cache timestamps or newly allocated arrays.
  const signature = JSON.stringify([
    scoreboard.score1, scoreboard.score2, scoreboard.cow1, scoreboard.cow2, scoreboard.cot,
    (nfl?.gameDays || []).flatMap(day => (day.games || []).map(item => [item.game, item.homeScore, item.visitorScore, item.scoreType, item.isComplete, item.isInProgress, item.gameQuarter, item.gameClock])),
  ])
  const previous = useRef({ load, signature })
  useEffect(() => {
    const changed = previous.current.load === load && previous.current.signature !== signature
    previous.current = { load, signature }
    if (!changed) return
    const timer = setTimeout(() => load(false), 250)
    return () => clearTimeout(timer)
  }, [signature, load])

  return { matchup, scoreboard, loading, error, refresh: load }
}

export default useMatchup
